import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Button,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import SystemUpdateIcon from '@mui/icons-material/SystemUpdate';
import currentMeta from 'src/meta.json';

const CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes

const VersionChecker = () => {
  const [hasUpdate, setHasUpdate] = useState(false);
  const [latestVersion, setLatestVersion] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const latestServerMetaRef = useRef(null);

  const checkVersion = useCallback(async () => {
    try {
      // Append cache-busting timestamp
      console.log('Checking for app update...');
      const response = await fetch(`/meta.json?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          Pragma: 'no-cache'
        }
      });

      if (!response.ok) return;

      const serverMeta = await response.json();
      latestServerMetaRef.current = serverMeta;

      const acknowledgedBuild = localStorage.getItem('acknowledged_build_time');
      const acknowledgedVersion = localStorage.getItem('acknowledged_version');
      const dismissedBuild = sessionStorage.getItem('dismissed_build_time');

      const serverBuildTime = Number(serverMeta.buildTime) || 0;
      const localBuildTime = Number(currentMeta.buildTime) || 0;
      const ackBuildTime = acknowledgedBuild ? Number(acknowledgedBuild) : 0;
      const dismBuildTime = dismissedBuild ? Number(dismissedBuild) : 0;
      console.log('serverBuildTime', serverBuildTime);
      console.log('localBuildTime', localBuildTime);
      console.log('ackBuildTime', ackBuildTime);
      // console.log("localBuildTime",localBuildTime)

      // Check if server buildTime is greater than local buildTime AND greater than acknowledged buildTime
      const isNewBuild =
        serverBuildTime > 0 &&
        (serverBuildTime > localBuildTime || serverBuildTime > ackBuildTime);

      // Check if server version differs from current local version AND acknowledged version
      const isNewVersion =
        Boolean(serverMeta.version) &&
        Boolean(currentMeta.version) &&
        serverMeta.version !== currentMeta.version &&
        serverMeta.version !== acknowledgedVersion;

      // Check if this build was dismissed in current session
      const isDismissedInSession =
        dismBuildTime > 0 && dismBuildTime >= serverBuildTime;

      // console.log("isNewBuild", isNewBuild, "isNewVersion", isNewVersion, "isDismissedInSession", isDismissedInSession);

      if ((isNewBuild || isNewVersion) && !isDismissedInSession) {
        // console.log('New build available');
        setHasUpdate(true);
        setLatestVersion(serverMeta.version || currentMeta.version);
      } else {
        setHasUpdate(false);
      }
    } catch (err) {
      console.warn('[VersionChecker] Failed to check for app update:', err);
    }
  }, []);

  useEffect(() => {
    // Initial check on mount
    checkVersion();

    // Interval check every CHECK_INTERVAL
    const intervalId = setInterval(checkVersion, CHECK_INTERVAL);

    // Sleep / Tab Wakeup Listeners: Immediately check version when user returns to tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkVersion();
      }
    };

    const handleFocus = () => {
      checkVersion();
    };

    // Catch script chunk loading failures when a new build deletes old bundle chunks
    const handleError = (event) => {
      const errorMsg = event?.message || event?.reason?.message || '';
      if (/Loading chunk \d+ failed|ChunkLoadError/i.test(errorMsg)) {
        setHasUpdate(true);
      }
    };

    const handleUnhandledRejection = (event) => {
      const reasonMsg = event?.reason?.message || event?.reason || '';
      if (/Loading chunk \d+ failed|ChunkLoadError/i.test(reasonMsg)) {
        setHasUpdate(true);
      }
    };

    window.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      clearInterval(intervalId);
      window.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('error', handleError);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection
      );
    };
  }, [checkVersion]);

  const handleRefresh = () => {
    const serverMeta = latestServerMetaRef.current;
    if (serverMeta) {
      if (serverMeta.buildTime) {
        localStorage.setItem(
          'acknowledged_build_time',
          String(serverMeta.buildTime)
        );
      }
      if (serverMeta.version) {
        localStorage.setItem(
          'acknowledged_version',
          String(serverMeta.version)
        );
      }
    } else {
      if (currentMeta.buildTime) {
        localStorage.setItem(
          'acknowledged_build_time',
          String(currentMeta.buildTime)
        );
      }
      if (currentMeta.version) {
        localStorage.setItem(
          'acknowledged_version',
          String(currentMeta.version)
        );
      }
    }
    window.location.reload();
  };

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setDismissed(true);
    const serverMeta = latestServerMetaRef.current;
    if (serverMeta && serverMeta.buildTime) {
      sessionStorage.setItem(
        'dismissed_build_time',
        String(serverMeta.buildTime)
      );
    }
  };

  console.log('hasUpdate>>>>>>>>', hasUpdate);
  console.log('dismissed>>>>>>>>>', dismissed);

  if (!hasUpdate || dismissed) {
    return null;
  }

  return (
    <Snackbar
      open={hasUpdate && !dismissed}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      sx={{
        mt: 2,
        zIndex: (theme) => theme.zIndex.tooltip + 1000
      }}
    >
      <Alert
        severity="info"
        variant="filled"
        icon={<SystemUpdateIcon fontSize="inherit" />}
        action={
          <Box display="flex" alignItems="center" gap={1}>
            <Button
              color="primary"
              variant="contained"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              sx={{
                bgcolor: 'common.white',
                color: 'info.main',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: 'grey.100'
                }
              }}
            >
              Reload
            </Button>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={handleClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        }
        sx={{
          width: '100%',
          maxWidth: 550,
          boxShadow: 6,
          borderRadius: 2,
          display: 'flex',
          alignItems: 'center',
          py: 1.5,
          px: 2.5,
          bgcolor: '#1e293b',
          color: '#ffffff',
          '& .MuiAlert-icon': {
            color: '#38bdf8',
            fontSize: '1.75rem'
          }
        }}
      >
        <AlertTitle sx={{ fontWeight: 'bold', mb: 0.5, color: '#f8fafc' }}>
          New Version Available {latestVersion ? `(v${latestVersion})` : ''}
        </AlertTitle>
        <Typography variant="body2" sx={{ color: '#cbd5e1' }}>
          A new update has been deployed. Please refresh your browser to use the
          latest features.
        </Typography>
      </Alert>
    </Snackbar>
  );
};

export default VersionChecker;
